
import { Vue, Component, Prop } from 'vue-property-decorator';
import { UploadDocument, DocumentTypeLabels, DocumentType } from '@/features/shipment/types/documentType';
import { getFileIcon, getFileExtension } from '@/features/shipment/helpers/documentValidation';
import { fetchHistoryDocument, extractFileName } from '@/features/shipment/services/documents.service';
import { showErrorAlertDialog } from '@/features/common/helpers';
import { parseError } from '@/features/common/helpers/errorParser';
import { writePreviewLoadingHtml } from '@/features/common/helpers/previewLoadingHtml';

@Component
export default class SupportingDocumentsHistory extends Vue {
  @Prop({ type: Array, required: true }) readonly documents!: UploadDocument[];

  @Prop({ type: String, required: true }) readonly trackingNumber!: string;

  // eslint-disable-next-line class-methods-use-this
  private getIcon(doc: UploadDocument): string {
    return getFileIcon(doc.originalFileName);
  }

  // eslint-disable-next-line class-methods-use-this
  private isPdf(doc: UploadDocument): boolean {
    return getFileExtension(doc.originalFileName) === 'pdf';
  }

  // eslint-disable-next-line class-methods-use-this
  private getTypeLabel(documentType: string): string {
    return DocumentTypeLabels[documentType as DocumentType] || documentType;
  }

  private isTypeTruncated(documentType: string): boolean {
    const label = this.getTypeLabel(documentType);
    return label.length > 15;
  }

  private async onView(documentId: string): Promise<void> {
    const newWindow = window.open('', '_blank');

    if (!newWindow) {
      showErrorAlertDialog('Popup blocked. Please allow popups for this site.');
      return;
    }

    try {
      const doc = this.documents.find((d) => d.documentId === documentId);
      writePreviewLoadingHtml(newWindow, {
        titleText: this.$t('documents.viewLoading') as string,
        hintText: this.$t('documents.previewLoadingHint') as string,
        tabTitle: doc?.originalFileName || (this.$t('documents.viewLoading') as string),
      });

      const response = await fetchHistoryDocument(this.trackingNumber, documentId);

      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const fileURL = URL.createObjectURL(blob);

      newWindow.location.href = fileURL;
      newWindow.document.title = doc?.originalFileName || 'Document';

      setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
    } catch (error) {
      newWindow.close();

      const errorResponse = parseError(error);
      const errorMessage = errorResponse.errorMessage
        || (this.$t('documents.errorMessages.viewFailed') as string);

      showErrorAlertDialog(errorMessage);
    }
  }

  private async onDownload(doc: UploadDocument): Promise<void> {
    try {
      const response = await fetchHistoryDocument(this.trackingNumber, doc.documentId);

      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const fileURL = URL.createObjectURL(blob);
      const fileName = doc.originalFileName || extractFileName(response.headers);

      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', fileName);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(fileURL), 100);
    } catch (error) {
      const errorResponse = parseError(error);
      showErrorAlertDialog(
        errorResponse.errorMessage
          || (this.$t('documents.errorMessages.downloadFailed') as string),
      );
    }
  }
}
